import { useEffect, useState } from 'react';
import { CheckCircle2, AlertCircle, Info, AlertTriangle, Loader2, X } from 'lucide-react';
import { getLastMessage, subscribeMessage, clearMessage } from '../lib/messageBus';

/**
 * Banner persistente che mostra l'ultimo messaggio (success/error/info/warning/loading)
 * generato da `toast.X(...)`. Resta visibile finché non ne arriva un altro.
 *
 * Posizionato accanto al nome dell'app nell'header di ogni pagina.
 * Compatto: testo su una riga + tooltip per il messaggio completo.
 */
export default function MessageBanner({ className = '' }) {
  const [msg, setMsg] = useState(getLastMessage());

  useEffect(() => {
    setMsg(getLastMessage());
    const unsub = subscribeMessage((m) => setMsg(m));
    return unsub;
  }, []);

  if (!msg || !msg.message) return null;

  const STYLES = {
    success: {
      bg: 'bg-emerald-50 border-emerald-200 text-emerald-800',
      icon: <CheckCircle2 className="h-4 w-4 text-emerald-600 flex-shrink-0" />,
    },
    error: {
      bg: 'bg-red-50 border-red-200 text-red-800',
      icon: <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />,
    },
    warning: {
      bg: 'bg-amber-50 border-amber-200 text-amber-800',
      icon: <AlertTriangle className="h-4 w-4 text-amber-600 flex-shrink-0" />,
    },
    info: {
      bg: 'bg-blue-50 border-blue-200 text-blue-800',
      icon: <Info className="h-4 w-4 text-blue-600 flex-shrink-0" />,
    },
    loading: {
      bg: 'bg-gray-50 border-gray-200 text-gray-800',
      icon: <Loader2 className="h-4 w-4 text-gray-600 animate-spin flex-shrink-0" />,
    },
  };
  const style = STYLES[msg.type] || STYLES.info;
  const fullText = msg.description ? `${msg.message} — ${msg.description}` : msg.message;
  const time = msg.timestamp ? new Date(msg.timestamp).toLocaleTimeString('it-IT', {
    hour: '2-digit', minute: '2-digit', second: '2-digit'
  }) : '';

  return (
    <div
      className={`hidden md:flex items-center gap-2 max-w-md min-w-0 px-3 py-1.5 border rounded-xl text-xs font-medium ${style.bg} ${className}`}
      title={`[${time}] ${fullText}`}
    >
      {style.icon}
      <span className="truncate flex-1">{msg.message}</span>
      {time && <span className="text-[11px] opacity-60 flex-shrink-0 tabular-nums">{time}</span>}
      <button aria-label="Nascondi"
        onClick={clearMessage}
        className="text-current opacity-50 hover:opacity-100 flex-shrink-0 rounded-full p-0.5 hover:bg-black/5"
        title="Nascondi"
      >
        <X className="h-3 w-3" />
      </button>
    </div>
  );
}
